// ManageConferences.js
import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { 
  getAllConferences, 
  createConference, 
  updateConference, 
  deleteConference, 
  getConferenceRegistrations, 
  updateRegistrationStatus, 
  deleteRegistration, 
  seedDefaultConferences 
} from '../../api/conferences';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import './ManageConferences.css';

const ManageConferences = () => {
  const [conferences, setConferences] = useState([]);
  const [selectedConference, setSelectedConference] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  
  // UI Loading/Status States
  const [loadingConfs, setLoadingConfs] = useState(true);
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [submittingConf, setSubmittingConf] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Modal / Form States
  const [showConfForm, setShowConfForm] = useState(false);
  const [editingConf, setEditingConf] = useState(null);
  const [confFormData, setConfFormData] = useState({
    name: '',
    dateLabel: '',
    location: '',
    sponsoredSlots: 5,
    active: true
  });

  useEffect(() => {
    fetchConferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedConference) {
      fetchRegistrations(selectedConference.id);
    } else {
      setRegistrations([]);
    }
  }, [selectedConference]);

  const fetchConferences = async () => {
    try {
      setLoadingConfs(true);
      setError(null);
      const data = await getAllConferences();
      setConferences(data);
      
      // Auto-select first conference or retain selection
      if (data.length > 0) {
        if (selectedConference) {
          const updatedSelected = data.find(c => c.id === selectedConference.id);
          setSelectedConference(updatedSelected || data[0]);
        } else {
          setSelectedConference(data[0]);
        }
      } else {
        setSelectedConference(null);
      }
    } catch (err) {
      console.error('Error fetching conferences:', err);
      setError('Failed to load conferences.');
    } finally {
      setLoadingConfs(false);
    }
  };

  const fetchRegistrations = async (confId) => {
    try {
      setLoadingRegs(true);
      const data = await getConferenceRegistrations(confId);
      setRegistrations(data);
    } catch (err) {
      console.error('Error fetching registrations:', err);
      setError('Failed to load registrations for the selected conference.');
    } finally {
      setLoadingRegs(false);
    }
  };

  const handleSeedConferences = async () => {
    try {
      setLoadingConfs(true);
      const seeded = await seedDefaultConferences();
      if (seeded) {
        setSuccess('Default conferences seeded successfully!');
        await fetchConferences();
      } else {
        setError('Conferences already exist. Seeding skipped.');
      }
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    } catch (err) {
      setError('Failed to seed default conferences.');
    } finally {
      setLoadingConfs(false);
    }
  };

  const handleConfInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOpenCreateForm = () => {
    setEditingConf(null);
    setConfFormData({
      name: '',
      dateLabel: '',
      location: '',
      sponsoredSlots: 5,
      active: true
    });
    setShowConfForm(true);
  };

  const handleOpenEditForm = (conf) => {
    setEditingConf(conf);
    setConfFormData({
      name: conf.name || '',
      dateLabel: conf.dateLabel || '',
      location: conf.location || '',
      sponsoredSlots: conf.sponsoredSlots || 5,
      active: conf.active !== false
    });
    setShowConfForm(true);
  };

  const handleConfFormSubmit = async (e) => {
    e.preventDefault();
    setSubmittingConf(true);
    setError(null);
    setSuccess(null);

    try {
      if (!confFormData.name || !confFormData.dateLabel || !confFormData.location) {
        setError('Please fill in all required fields.');
        setSubmittingConf(false);
        return;
      }

      if (editingConf) {
        await updateConference(editingConf.id, confFormData);
        setSuccess('Conference updated successfully!');
      } else {
        await createConference(confFormData);
        setSuccess('Conference created successfully!');
      }

      setShowConfForm(false);
      await fetchConferences();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving conference:', err);
      setError(err.message || 'Failed to save conference.');
    } finally {
      setSubmittingConf(false);
    }
  };

  const handleDeleteConf = async (confId) => {
    if (!window.confirm('WARNING: Deleting this conference will delete ALL associated student/research registrations and their uploaded abstract PDFs. Are you sure you want to proceed?')) {
      return;
    }

    try {
      setLoadingConfs(true);
      await deleteConference(confId);
      setSuccess('Conference deleted successfully!');
      if (selectedConference?.id === confId) {
        setSelectedConference(null);
      }
      await fetchConferences();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting conference:', err);
      setError('Failed to delete conference.');
    } finally {
      setLoadingConfs(false);
    }
  };

  const handleStatusChange = async (regId, newStatus) => {
    try {
      await updateRegistrationStatus(regId, newStatus, selectedConference.id);
      setSuccess('Registration status updated successfully!');
      
      // Refresh registrations and conferences (to update counts)
      await Promise.all([
        fetchRegistrations(selectedConference.id),
        fetchConferences()
      ]);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update registration status.');
    }
  };

  const handleDeleteRegistration = async (regId) => {
    if (!window.confirm('Are you sure you want to delete this registration? The associated PDF proof will be deleted from Storage.')) {
      return;
    }

    try {
      await deleteRegistration(regId, selectedConference.id);
      setSuccess('Registration deleted successfully!');
      
      // Refresh
      await Promise.all([
        fetchRegistrations(selectedConference.id),
        fetchConferences()
      ]);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting registration:', err);
      setError('Failed to delete registration.');
    }
  };

  const handleExportCSV = () => {
    if (registrations.length === 0) {
      alert('No registrations available to export.');
      return;
    }
    
    const csvData = registrations.map((reg, index) => ({
      'No.': index + 1,
      'Arabic Name': reg.fullNameArabic,
      'English Name': reg.fullNameEnglish,
      'Job Title': reg.jobTitle,
      'Workplace': reg.workplace,
      'Phone (WhatsApp)': reg.phone,
      'Email': reg.email,
      'Registered Member': reg.isMember,
      'Previously Represented': reg.previouslyRepresented,
      'Presentation Title': reg.researchTitle,
      'PDF Acceptance Proof URL': reg.pdfUrl,
      'Status': reg.status === 'primary' ? 'Primary' : 'Reserve (Waiting List)',
      'Registration Date': new Date(reg.createdAt).toLocaleString('en-US')
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const sanitizedTitle = selectedConference.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${sanitizedTitle}_registrations.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loadingConfs && conferences.length === 0) {
    return (
      <div className="admin-confs-loading">
        <div className="loading-spinner"></div>
        <p>Loading conferences dashboard...</p>
      </div>
    );
  }

  return (
    <div className="manage-confs-page">
      <div className="page-header-row">
        <div>
          <h1>Manage Conferences & Applications</h1>
          <p>Configure scientific conference sponsored slots and review applications.</p>
        </div>
        <div className="header-actions">
          {conferences.length === 0 && (
            <Button variant="outline" onClick={handleSeedConferences} className="seed-btn">
              🌱 Seed Default Conferences
            </Button>
          )}
          <Button variant="primary" onClick={handleOpenCreateForm}>
            ➕ Add Conference
          </Button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Grid of Conferences Overview */}
      <div className="confs-grid">
        <div className="glass-card list-card">
          <h2>Conferences List</h2>
          {conferences.length === 0 ? (
            <p className="empty-msg">No conferences configured. Click "Add Conference" or "Seed Default Conferences" to start.</p>
          ) : (
            <div className="confs-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Conference Name</th>
                    <th>Slots (Primary/Max)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {conferences.map(conf => (
                    <tr 
                      key={conf.id} 
                      className={selectedConference?.id === conf.id ? 'selected-row' : ''}
                      onClick={() => setSelectedConference(conf)}
                    >
                      <td>
                        <div className="conf-name-cell">
                          <strong>{conf.name}</strong>
                          <span className="conf-meta">{conf.dateLabel} | {conf.location}</span>
                        </div>
                      </td>
                      <td>
                        <span className="slots-counter">
                          {conf.primaryRegisteredCount || 0} / {conf.sponsoredSlots || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-pill ${conf.active !== false ? 'active' : 'inactive'}`}>
                          {conf.active !== false ? 'Active' : 'Archived'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="row-actions">
                          <button 
                            className="btn-icon" 
                            title="Edit"
                            onClick={() => handleOpenEditForm(conf)}
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-icon delete" 
                            title="Delete"
                            onClick={() => handleDeleteConf(conf.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Selected Conference Stats Dashboard */}
        {selectedConference && (
          <div className="glass-card stats-card">
            <h2>{selectedConference.name} Statistics</h2>
            <div className="stats-box-grid">
              <div className="stat-box primary">
                <h3>Sponsored Limit</h3>
                <p>{selectedConference.sponsoredSlots}</p>
              </div>
              <div className="stat-box success">
                <h3>Primary Applicants</h3>
                <p>{selectedConference.primaryRegisteredCount || 0}</p>
              </div>
              <div className="stat-box warning">
                <h3>Reserve (Waiting)</h3>
                <p>{Math.max(0, registrations.length - (selectedConference.primaryRegisteredCount || 0))}</p>
              </div>
              <div className="stat-box info">
                <h3>Total Submissions</h3>
                <p>{registrations.length}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Registrations List Section */}
      {selectedConference && (
        <div className="glass-card registrations-section">
          <div className="section-header-row">
            <div>
              <h2>Applicants List ({registrations.length})</h2>
              <p>Showing registrations for <strong>{selectedConference.name}</strong> sorted by registration date.</p>
            </div>
            {registrations.length > 0 && (
              <Button variant="outline" onClick={handleExportCSV} className="export-btn">
                📥 Export to CSV
              </Button>
            )}
          </div>

          {loadingRegs ? (
            <div className="regs-loading">
              <div className="loading-spinner"></div>
              <p>Loading registrations data...</p>
            </div>
          ) : registrations.length === 0 ? (
            <p className="empty-msg">No applications submitted for this conference yet.</p>
          ) : (
            <div className="regs-table-wrapper">
              <table className="admin-table text-small">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Contact Info</th>
                    <th>Job & Workplace</th>
                    <th>Member / Represented</th>
                    <th>Research Title & Abstract Proof</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, index) => (
                    <tr key={reg.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="name-cell">
                          <span className="english-name">{reg.fullNameEnglish}</span>
                          <span className="arabic-name" dir="rtl">{reg.fullNameArabic}</span>
                        </div>
                      </td>
                      <td>
                        <div className="contact-cell">
                          <span>📧 {reg.email}</span>
                          <span>💬 {reg.phone}</span>
                        </div>
                      </td>
                      <td>
                        <div className="job-cell">
                          <strong>{reg.jobTitle}</strong>
                          <span>@{reg.workplace}</span>
                        </div>
                      </td>
                      <td>
                        <div className="badge-cell">
                          <span className={`badge ${reg.isMember === 'Yes' ? 'green' : 'gray'}`}>
                            Member: {reg.isMember}
                          </span>
                          <span className={`badge ${reg.previouslyRepresented === 'Yes' ? 'orange' : 'gray'}`}>
                            Represented: {reg.previouslyRepresented}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="research-cell">
                          <span className="research-title" title={reg.researchTitle}>
                            "{reg.researchTitle}"
                          </span>
                          {reg.pdfUrl ? (
                            <a 
                              href={reg.pdfUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="pdf-link"
                            >
                              📄 View Abstract Proof PDF
                            </a>
                          ) : (
                            <span className="no-pdf">No PDF Uploaded</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <select
                          className={`status-select ${reg.status}`}
                          value={reg.status}
                          onChange={(e) => handleStatusChange(reg.id, e.target.value)}
                        >
                          <option value="primary">Primary</option>
                          <option value="reserve">Reserve (Waiting)</option>
                          <option value="withdrawn">Withdrawn</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td>
                        <button 
                          className="btn-icon delete" 
                          title="Delete Registration"
                          onClick={() => handleDeleteRegistration(reg.id)}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Conference Create/Edit Modal Overlay */}
      {showConfForm && (
        <div className="modal-overlay">
          <div className="modal-content glass-card">
            <h2>{editingConf ? 'Edit Conference' : 'Add New Conference'}</h2>
            
            <form onSubmit={handleConfFormSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Conference Name *</label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="e.g. SOS Conference 2026"
                  value={confFormData.name}
                  onChange={handleConfInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="dateLabel">Date Label / Period *</label>
                <Input
                  type="text"
                  id="dateLabel"
                  name="dateLabel"
                  placeholder="e.g. 5 to 7 November 2026"
                  value={confFormData.dateLabel}
                  onChange={handleConfInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">Location *</label>
                <Input
                  type="text"
                  id="location"
                  name="location"
                  placeholder="e.g. Al Khobar, Saudi Arabia"
                  value={confFormData.location}
                  onChange={handleConfInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="sponsoredSlots">Sponsored Slots Capacity *</label>
                <Input
                  type="number"
                  id="sponsoredSlots"
                  name="sponsoredSlots"
                  min="0"
                  placeholder="e.g. 3"
                  value={confFormData.sponsoredSlots}
                  onChange={handleConfInputChange}
                  required
                />
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={confFormData.active}
                  onChange={handleConfInputChange}
                />
                <label htmlFor="active">Active (Visible in Registration Form)</label>
              </div>

              <div className="modal-actions">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowConfForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={submittingConf}
                >
                  {submittingConf ? 'Saving...' : 'Save Conference'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageConferences;
